<script setup lang="ts">
import {
  formatting,
  LegacyAdjustmentCategory,
  LegacyOrder,
  legacyOrderCommands,
  legacyOrderKey,
  LegacyOrderRow
} from '@hantera/portal-app';
import {computed, inject, ref, watchEffect} from 'vue';
import OrderAdjustmentForm from '../OrderAdjustmentForm';
import {EditableOrderRule} from '../editable-order-adjustment';
import Decimal from 'decimal.js';
import useBackend from '@/composition/useBackend';
import {DecimalInputValue} from "@hantera/design-system";

const props = defineProps<{
  rule: EditableOrderRule
  orderRow: LegacyOrderRow
}>();

const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));

const { currency, percent } = formatting;
const backend = useBackend();

const showForm = ref(false);
const category = ref<LegacyAdjustmentCategory>();
const adjustmentRow = computed(() => {
  return props.rule.affectedRows.find(r => r.rowId === props.orderRow.rowId);
});

const value = ref<DecimalInputValue>({
  value: new Decimal(0),
  isPercentage: false,
})

watchEffect(() => {
  value.value = {
    value: props.rule.value,
    isPercentage: false
  }
})

resetForm();

const isEditable = computed(() => {
  // HACK: We assume that any row that's manual and row based is a manual row adjustment.
  // This can back-fire but we don't have any other means until we gotten rid of field definitions and can use our
  // own rule field to track this. This hack is also represented in OrderSummary.
  return props.rule.editable && adjustmentRow.value?.isRowBased;
});

function openForm() {
  if (isEditable.value) {
    showForm.value = true;
  }
}

function resetForm() {
  props.rule.value = props.rule.originalValue;
  if (adjustmentRow.value) {
    adjustmentRow.value.value = adjustmentRow.value.originalValue;
  }

  // Something seems buggy in Hantera backend and the fields are not returned, so
  // we'll match on rule name which is the best we can do right now.
  category.value = order.value.adjustmentCategories.find(r => r.name === props.rule.name);
}

function closeForm() {
  resetForm();
  showForm.value = false;
}

const qty = computed(() => {
  return props.orderRow.quantity.sub(props.orderRow.returnedQuantity).sub(props.orderRow.lostQuantity);
});

const listingTotal = computed(() => {
  return props.orderRow.unitPrice.mul(qty.value);
});

function formatAdjustmentPercentage() {
  if (!adjustmentRow.value) {
    return;
  }
  if (listingTotal.value.eq(0)) {
    return percent(new Decimal(0));
  }
  return percent(adjustmentRow.value.value.dividedBy(listingTotal.value));
}

function formatAdjustmentUnit() {
  if (!adjustmentRow.value) {
    return;
  }
  if (listingTotal.value.eq(0)) {
    return currency(new Decimal(0), order.value.currencyCode);
  }
  return currency(
    adjustmentRow.value.value.dividedBy(qty.value), order.value.currencyCode
  );
}

async function submit() {
  if (!adjustmentRow.value) { return; }

  if (adjustmentRow.value.value.eq(new Decimal(0))) {
    await backend.legacyOrders.buildCommandGroup(
      order.value.orderId,
      'Remove row adjustment of {value}',
      {
        value: adjustmentRow.value.originalValue
      }
    )
      .command(legacyOrderCommands.removeAdjustment, {
        adjustmentId: props.rule.adjustmentId,
      })
      .command(legacyOrderCommands.addActivityLog, {
        message: 'Removed row adjustment \'{name}\' with value {value}',
        fields: {
          name: props.rule.name,
          value: props.rule.originalValue,
        }
      })
      .send();

    closeForm();
    return;
  }

  if (!category.value) {
    // TODO Add error state
    return;
  }

  if (!order.value.includeVat) {
    // Turns out that accounting for VAT here causes more problems than it solves as legacy backend will mix things up / Kristoffer, 2025-03-29
    //priceWithVat = adjustmentRow.value.value.mul(orderRow.vatPercentage.add(1));
  }

  await backend.legacyOrders.buildCommandGroup(
    order.value.orderId,
    'Set row adjustment from {oldValue} to {newValue}',
    {
      oldValue: adjustmentRow.value.originalValue,
      newValue: value.value.value + (value.value.isPercentage ? '%' : ''),
    }
  )
    .command(legacyOrderCommands.addOrUpdateAdjustment, {
      adjustmentId: props.rule.adjustmentId,
      percentage: value.value.isPercentage ? value.value.value.div(100) : undefined,
      priceWithVat: value.value.isPercentage ? undefined : value.value.value,
      affectedRows: [props.orderRow.rowId],
    })
    // TODO: Add Description and it's UI (if users miss it - otherwise we should drop this for now, maybe some kind of notes will be needed in the future but "Description" is a bit misleading)
    // .command(upsertRmaAdjustmentField, {
    //   rmaId: props.data.rmaId,
    //   rmaAdjustmentId: adjustmentId,
    //   fieldKey: 'Description',
    //   fieldValue: '',
    // })
    .command(legacyOrderCommands.upsertAdjustmentField, {
      adjustmentId: props.rule.adjustmentId,
      fieldKey: 'CategoryCode',
      fieldValue: category.value.code,
    })
    .command(legacyOrderCommands.upsertAdjustmentField, {
      adjustmentId: props.rule.adjustmentId,
      fieldKey: 'CategoryDescription',
      fieldValue: category.value.name,
    })
    .command(legacyOrderCommands.addActivityLog, {
      message: 'Set row adjustment {name} from {oldValue} to {newValue}',
      fields: {
        oldValue: adjustmentRow.value.originalValue,
        newValue: value.value.value + (value.value.isPercentage ? '%' : ''),
        name: category.value.name,
      }
    })
    .send();

  closeForm();
  return;
}
</script>

<template>
  <!-- We deliberately display 0-sum adjustments as
  otherwise adjustments disappear when a row is fully
  returned. Once the problem of 0 unit price is fixed
  with new API then this would be a non-issue, as the
  adjustment will have a unit adjustment but have a 0
  sum. -->
  <div
    class="legacy-order-row-adjustment"
    v-if="adjustmentRow"
  >
    <template v-if="!showForm">
      <div
        class="legacy-order-row-adjustments__name"
        v-if="!showForm"
      >
        <span
          :class="{
            'btn-editable': isEditable
          }"
          @click="openForm"
          :title="rule.name"
        >{{ rule.name }}</span>
      </div>

      <div class="legacy-order-row-adjustments__percentage">
        {{ formatAdjustmentPercentage() }}
      </div>
      <div class="legacy-order-row-adjustments__unit">{{ formatAdjustmentUnit() }}</div>
      <div class="legacy-order-row-adjustments__total">{{ currency(adjustmentRow.value, order.currencyCode) }}</div>
    </template>

    <OrderAdjustmentForm
      v-if="showForm"
      v-model:selected-category="category"
      v-model:value="value"
      @submit="submit"
      @cancel="closeForm"
      class="legacy-order-row-adjustment__form"
    />
  </div>
</template>

<style>
.legacy-order-row-adjustment {
  display: contents;
}

.legacy-order-row-adjustment__form {
  grid-column: 1 / 5;
}
</style>